import { DeveloperHeader } from "../../components/DeveloperHeader";
import { Box, Container, Grid, Link, Typography } from "@mui/material";
import SideBar from "../../components/Setting/Sidebar";
import Divider from "@mui/material/Divider";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { AddAPIKeyModal } from "../../components/APIKey/AddAPIKeyModal";
import { UpdateAPIKeyModal } from "../../components/APIKey/UpdateAPIKeyModal";
import { RemoveAPIKeyModal } from "../../components/APIKey/RemoveAPIKeyModal";
import { useDeveloperContext } from "../../hook/useDeveloperContext";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../../state/ShowProgress";
import { Apikey } from "../../openApi/api";
import { apiKeyApi } from "../../openApi";
import { HTTP_RESPONSE_CODE } from "../../common/const";
import { toast } from "react-toastify";
import { getFormattedDate } from "../../common/helper";
import { ThemeLight } from "../../state/ThemeLight";

export const APIKeys = () => {
  const { userID, sessionKey, handleSessionKey } = useDeveloperContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);
  const [themeLight] = useRecoilState(ThemeLight);

  const [apiKeys, setApiKeys] = useState<Apikey[]>([]);

  const [showAddKeyModal, setShowAddKeyModal] = useState<boolean>(false);
  const [showUpdateKeyModal, setShowUpdateKeyModal] = useState<boolean>(false);
  const [showRemoveKeyModal, setShowRemoveKeyModal] = useState<boolean>(false);

  const [selectedIndex, setSelectedIndex] = useState<number>();

  useEffect(() => {
    if (sessionKey !== undefined && sessionKey !== "" && userID !== 0) {
      handleRefresh();
    }
  }, [userID, sessionKey]);

  const handleRefresh = () => {
    setShowProgress(true);
    apiKeyApi
      .getKeys(userID.toString(), sessionKey!)
      .then((res) => {
        setShowProgress(false);
        setApiKeys(res.data.keys ?? []);
      })
      .catch((err) => {
        setShowProgress(false);
        if (err.response) {
          if (
            err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
          ) {
            toast.error("Session key is expired", {
              theme: themeLight ? "light" : "dark",
            });
            handleSessionKey("");
          } else {
            toast.error(err.response.data.message, {
              theme: themeLight ? "light" : "dark",
            });
          }
        } else {
          toast.error(err.message, { theme: themeLight ? "light" : "dark" });
        }
      });
  };

  return (
    <>
      <DeveloperHeader title="API Keys" />

      <Container className="container-xxl pb-16 mt-n14">
        <Grid container spacing={2}>
          <Grid item xl={3} lg={4}>
            <SideBar />
          </Grid>
          <Grid item xl={9} lg={8}>
            <Box className="card mb-4">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <Typography className="card-header-title">
                  API Keys
                  <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                    <span id="ContentPlaceHolder1_litQuataLimitNumber">
                      3 limit
                    </span>
                  </span>
                </Typography>
                {apiKeys.length < 3 && (
                  <Link
                    href="#"
                    id="ContentPlaceHolder1_addnew"
                    className="btn btn-primary text-nowrap d-flex width-fit-content"
                    onClick={() => setShowAddKeyModal(true)}
                  >
                    <AddIcon className="text-icon my-auto" /> Add
                  </Link>
                )}
              </Box>
              <Box className="card-body">
                <p>
                  For developers interested in building applications using our{" "}
                  <Link href="#" target="_blank">
                    API Service
                  </Link>
                  , please create an API-Key Token which you can then use with
                  all your API requests.
                </p>

                <Box className="d-flex flex-column flex-md-row align-items-md-center gap-3"></Box>
              </Box>

              <Box className="table-responsive">
                <table className="table table-hover">
                  <thead
                    id="ContentPlaceHolder1_theadApiKey"
                    className="text-nowrap"
                  >
                    <tr>
                      <th scope="col">App Name</th>
                      <th scope="col">API Key Token</th>
                      <th scope="col">API Statistics</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((key, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <span id="spanAppName_3CRVCMSZWZDFDF5ZPPM6FKF2VSS43MVZIR">
                              {key.appName}
                            </span>
                          </td>
                          <td>
                            <Box className="d-flex align-items-center">
                              <span className="me-2">{key.apiKey}</span>
                              <Link
                                className="js-clipboard link-secondary"
                                href="#"
                                onClick={() => {
                                  navigator.clipboard
                                    .writeText(key.apiKey)
                                    .then(() =>
                                      toast.success("Copied", {
                                        theme: themeLight ? "light" : "dark",
                                      })
                                    );
                                }}
                              >
                                <ContentCopyIcon className="fa-fw" />{" "}
                              </Link>
                            </Box>
                            <Box className="text-muted small">
                              Added on {getFormattedDate(key.createdAt)}
                            </Box>
                          </td>
                          <td>
                            <Link
                              className="btn btn-sm btn-outline-primary text-nowrap me-1 d-flex width-fit-content"
                              href="#"
                            >
                              <SignalCellularAltIcon className="statistic-icon me-1 my-auto" />{" "}
                              Visit Stats
                            </Link>
                          </td>
                          <td className="text-end text-nowrap">
                            <Link
                              className="btn btn-sm btn-white me-1"
                              href="#"
                              onClick={() => {
                                setSelectedIndex(index);
                                setShowUpdateKeyModal(true);
                              }}
                            >
                              <EditIcon className="fa-fw" />
                            </Link>
                            <Link
                              className="btn btn-sm btn-white text-danger "
                              href="#"
                              onClick={() => {
                                setSelectedIndex(index);
                                setShowRemoveKeyModal(true);
                              }}
                            >
                              <DeleteIcon className="fa-fw my-auto" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>

              {apiKeys.length === 3 && (
                <Box className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Box className="d-flex align-items-center gap-2">
                    <span className="text-muted">
                      &nbsp;Limit of 3 api-key tokens quota reached
                    </span>
                  </Box>
                </Box>
              )}
            </Box>

            <Box className="card mb-4">
              <Box className="card-header">
                <Typography className="card-header-title">
                  Current API Plans
                </Typography>
              </Box>
              <Box className="card-body">
                <p className="text-dark mb-4">
                  All API plan accounts are FREE by default. You may upgrade
                  your plan to API Pro at any time.
                </p>
                <Box className="row align-items-center">
                  <Box className="col-md-4 mb-1 mb-md-0">My API Plan:</Box>
                  <Box className="col-md-8">
                    <Box className="d-sm-flex justify-content-between align-items-center">
                      <strong className="text-uppercase me-3">
                        FREE API Plan
                      </strong>
                    </Box>
                  </Box>
                </Box>
                <Divider className="my-2" />
                <Box className="row align-items-center">
                  <Box className="col-md-4 mb-1 mb-md-0">
                    API calls per second:
                  </Box>
                  <Box className="col-md-8">
                    <strong>5 calls</strong>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className="alert alert-dark d-flex align-items-baseline mb-4">
              <i className="far fa-info-circle me-2"></i>
              <span>
                API keys created on explorer.upcx.io can be used for the UPCX
                Testnets. Detailed documentation to get started can be found at{" "}
                <Link
                  target="_blank"
                  href="https://explorer.upcx.org:5000/api/"
                >
                  API Documentation
                </Link>
                . If you need any API support, contact us{" "}
                <Link href="/contactus?id=11">here</Link>.
              </span>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <AddAPIKeyModal
        open={showAddKeyModal}
        handleOpen={setShowAddKeyModal}
        handleRefresh={handleRefresh}
      />
      <UpdateAPIKeyModal
        open={showUpdateKeyModal}
        handleOpen={setShowUpdateKeyModal}
        handleRefresh={handleRefresh}
        apiKey={
          selectedIndex !== undefined && selectedIndex < apiKeys.length
            ? apiKeys[selectedIndex]
            : undefined
        }
      />
      <RemoveAPIKeyModal
        open={showRemoveKeyModal}
        handleOpen={setShowRemoveKeyModal}
        handleRefresh={handleRefresh}
        apiKey={
          selectedIndex !== undefined && selectedIndex < apiKeys.length
            ? apiKeys[selectedIndex]
            : undefined
        }
      />
    </>
  );
};
